"use client";

import { useState } from "react";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth-context";
import { deleteCurrentUserAccount } from "@eventify/shared/services/firebaseAuth";

const CONTACT_EMAIL = "privacy@therunup.ai";

export default function DataDeletionContent() {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();

  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleted, setDeleted] = useState(false);

  const handleSelfDelete = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      const result = await deleteCurrentUserAccount();
      if (result.success) {
        setDeleted(true);
        setConfirming(false);
      } else {
        const msg = result.error || "";
        setDeleteError(
          /recent|sign in again/i.test(msg)
            ? t("web.dataDeletion.selfService.errorRecentLogin")
            : t("web.dataDeletion.selfService.error"),
        );
      }
    } catch {
      setDeleteError(t("web.dataDeletion.selfService.error"));
    } finally {
      setDeleting(false);
    }
  };

  const emailLink = (
    <a
      href={`mailto:${CONTACT_EMAIL}?subject=Data%20Deletion%20Request`}
      className="text-primary hover:underline"
    >
      {CONTACT_EMAIL}
    </a>
  );

  const withEmail = (key: string) => {
    const parts = t(key, { email: "__EMAIL__" }).split("__EMAIL__");
    return parts.flatMap((part, i) =>
      i < parts.length - 1
        ? [<span key={`p${i}`}>{part}</span>, <span key={`e${i}`}>{emailLink}</span>]
        : [<span key={`p${i}`}>{part}</span>],
    );
  };

  return (
    // Semantic <main> lives in the root layout to avoid nested <main> elements.
    <div className="bg-surface min-h-screen">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <Link href="/" className="text-sm text-primary hover:underline">
          &larr; {t("web.dataDeletion.backToHome")}
        </Link>

        <h1 className="mt-6 text-4xl font-headline font-bold text-on-surface">
          {t("web.dataDeletion.title")}
        </h1>
        <p className="mt-2 text-sm text-on-surface-variant">
          {t("web.dataDeletion.lastUpdated")}: {t("web.dataDeletion.lastUpdatedDate")}
        </p>

        <div className="mt-10 space-y-8 text-on-surface leading-relaxed">
          <section className="bg-surface-container-lowest rounded-card shadow-card p-8">
            <p>{t("web.dataDeletion.intro")}</p>
          </section>

          {(isAuthenticated || deleted) && (
            <section className="bg-surface-container-lowest rounded-card shadow-card p-8">
              <h2 className="text-2xl font-headline font-semibold mb-3">
                {t("web.dataDeletion.selfService.heading")}
              </h2>
              {deleted ? (
                <p className="text-primary font-medium">
                  {t("web.dataDeletion.selfService.success")}
                </p>
              ) : (
                <>
                  <p className="mb-2 text-sm text-on-surface-variant">
                    {t("web.dataDeletion.selfService.signedInAs", {
                      email: user?.email || "",
                    })}
                  </p>
                  <p className="mb-4">{t("web.dataDeletion.selfService.body")}</p>
                  {deleteError && <p className="mb-4 text-error">{deleteError}</p>}
                  {confirming ? (
                    <div className="ghost-border rounded-card p-4">
                      <p className="mb-4 font-medium">
                        {t("web.dataDeletion.selfService.confirmBody")}
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={handleSelfDelete}
                          disabled={deleting}
                          className="rounded-full bg-error px-6 py-3 text-base font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50"
                        >
                          {deleting
                            ? t("web.dataDeletion.selfService.deleting")
                            : t("web.dataDeletion.selfService.confirmYes")}
                        </button>
                        <button
                          onClick={() => setConfirming(false)}
                          disabled={deleting}
                          className="rounded-full bg-surface-container px-6 py-3 text-base font-medium text-on-surface-variant transition-all hover:bg-surface-container-high disabled:opacity-50"
                        >
                          {t("web.dataDeletion.selfService.cancel")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirming(true)}
                      className="rounded-full bg-error px-6 py-3 text-base font-semibold text-white transition-all hover:opacity-90"
                    >
                      {t("web.dataDeletion.selfService.button")}
                    </button>
                  )}
                </>
              )}
            </section>
          )}

          <section className="bg-surface-container-lowest rounded-card shadow-card p-8">
            <h2 className="text-2xl font-headline font-semibold mb-3">
              {t("web.dataDeletion.howToRequest.heading")}
            </h2>
            <p>{withEmail("web.dataDeletion.howToRequest.body")}</p>
          </section>

          <section className="bg-surface-container-lowest rounded-card shadow-card p-8">
            <h2 className="text-2xl font-headline font-semibold mb-3">
              {t("web.dataDeletion.timeframe.heading")}
            </h2>
            <p>{t("web.dataDeletion.timeframe.body")}</p>
          </section>

          <section className="bg-surface-container-lowest rounded-card shadow-card p-8">
            <h2 className="text-2xl font-headline font-semibold mb-3">
              {t("web.dataDeletion.retention.heading")}
            </h2>
            <p>{t("web.dataDeletion.retention.body")}</p>
          </section>

          <section className="bg-surface-container-lowest rounded-card shadow-card p-8">
            <h2 className="text-2xl font-headline font-semibold mb-3">
              {t("web.dataDeletion.contact.heading")}
            </h2>
            <p>{withEmail("web.dataDeletion.contact.body")}</p>
          </section>
        </div>
      </div>
    </div>
  );
}
